import React, { useState, useRef, useEffect } from "react";
import Select from "react-select";
import { FaCalendar } from "react-icons/fa6";
import { FaTimes } from "react-icons/fa";
import { useSelector } from "react-redux";
import {
  downloadCBReport,
  getOrganisation,
  getAllUsers,
} from "../../../config/LiveApi";
import { toast } from "react-toastify";
import { DateRangePicker } from "react-date-range";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";

const styles = {
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.5)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 9999,
  },

  modal: {
    background: "#fff",
    width: "850px",
    maxWidth: "95vw",
    borderRadius: "12px",
    padding: "24px",
    position: "relative",
    minHeight: "80vh",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden", // prevent double scrollbar
  },

  closeBtn: {
    position: "absolute",
    right: "15px",
    top: "15px",
    border: "none",
    background: "transparent",
    cursor: "pointer",
  },

  heading: {
    marginBottom: "20px",
    fontSize: "22px",
    fontWeight: 600,
  },
  
  section: {
    marginBottom: "16px",
  },

  label: {
    display: "block",
    marginBottom: "8px",
    fontWeight: 600,
    fontSize: "14px",
  },

  dateRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "16px",
    padding: "12px 16px",
    border: "1px solid #e5e7eb",
    borderRadius: "10px",
    background: "#fafafa",
    position: "relative",
  },

  calendarBtn: {
    width: "42px",
    height: "42px",
    border: "1px solid #d1d5db",
    borderRadius: "8px",
    background: "#fff",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "16px",
  },

  pickerWrapper: {
    position: "absolute",
    top: "70px",
    right: "0",
    zIndex: 1000,
    background: "#fff",
    borderRadius: "12px",
    boxShadow: "0 10px 30px rgba(0,0,0,0.12)",
    border: "1px solid #e5e7eb",
    overflow: "hidden",
  },

  pickerFooter: {
    padding: "12px 16px",
    borderTop: "1px solid #e5e7eb",
    display: "flex",
    justifyContent: "flex-end",
    background: "#fafafa",
  },

  applyBtn: {
    padding: "10px 18px",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    fontWeight: 600,
    background: "#fc8019",
  },

  downloadWrapper: {
    display: "flex",
    justifyContent: "flex-end",
    flexShrink: 0,
    marginTop: "auto", // pushed to bottom in column
  },

  downloadBtn: {
    padding: "12px 20px",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    fontWeight: 600,
    background: "#fc8019",
  },
};

const DownloadReportModal = ({ onClose }) => {
  const token = useSelector((store) => store.userReducer.token);
  const role = useSelector((store) => store.userReducer.role);

  const pickerRef = useRef();

  const [showPicker, setShowPicker] = useState(false);
  const [loadingFilters, setLoadingFilters] = useState(false);

  const [orgs, setOrgs] = useState([]);
  const [merchants, setMerchants] = useState([]);

  const [selectedOrgs, setSelectedOrgs] = useState([]);
  const [selectedMerchants, setSelectedMerchants] = useState([]);

  const [tempRange, setTempRange] = useState({
    startDate: new Date(new Date().setHours(0, 0, 0, 0)),
    endDate: new Date(new Date().setHours(23, 59, 59, 999)),
    key: "selection",
  });

  const [selectedRange, setSelectedRange] = useState(tempRange);
  const [isDownloading, setIsDownloading] = useState(false);
  useEffect(() => {
    const fetchFilters = async () => {
      if (!["super", "operation", "compliance"].includes(role)) return;

      try {
        setLoadingFilters(true);

        const [orgRes, merchantRes] = await Promise.all([
          getOrganisation(token),
          getAllUsers(token, 1),
        ]);

        const allUsers = merchantRes?.data || [];

        setOrgs(orgRes?.data || []);

        setMerchants(allUsers.filter((user) => user.role === "admin"));
      } catch (err) {
        console.error(err);
      } finally {
        setLoadingFilters(false);
      }
    };

    fetchFilters();
  }, [token, role]);

  const handleSelect = (ranges) => {
    setTempRange(ranges.selection);
  };

  const handleApply = () => {
    const start = new Date(tempRange.startDate);
    const end = new Date(tempRange.endDate);

    const diffInDays =
      (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24);

    if (diffInDays > 183) {
      toast.error("Maximum allowed date range is 6 months");
      return;
    }
    setSelectedRange(tempRange);
    setShowPicker(false);
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target)) {
        setShowPicker(false);
      }
    };

    if (showPicker) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showPicker]);

  const orgOptions = [
    { value: "__all__", label: "All Organizations" },
    ...orgs.map((org) => ({
      value: org.orgName,
      label: org.orgName,
    })),
  ];

  const merchantOptions = [
    { value: "__all__", label: "All Merchants" },
    ...merchants.map((merchant) => ({
      value: merchant.id,
      label: merchant.alias,
    })),
  ];

  const handleDownload = async () => {
    const loadingToast = toast.loading("Generating report...");
    const formattedStart = selectedRange.startDate.toISOString();

    const adjustedEndDate = new Date(selectedRange.endDate);
    adjustedEndDate.setDate(adjustedEndDate.getDate() + 1);

    const formattedEnd = adjustedEndDate.toISOString();

    const orgIds = selectedOrgs.map((item) => item.value);
    const merchantIds = selectedMerchants.map((item) => item.value);

    try {
      setIsDownloading(true);
      const response = await downloadCBReport(
        token,
        formattedStart,
        formattedEnd,
        orgIds,
        merchantIds,
      );

      const blob = new Blob([response], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "reconciliation_report.xlsx");

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.update(loadingToast, {
        render: "Report downloaded successfully",
        type: "success",
        isLoading: false,
        autoClose: 3000,
      });
      onClose();
    } catch (err) {
      toast.update(loadingToast, {
        render: "Failed to download report",
        type: "error",
        isLoading: false,
        autoClose: 3000,
      });
      console.error(err);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        <button onClick={onClose} style={styles.closeBtn}>
          <FaTimes size={22} />
        </button>

        <h2 style={styles.heading}>Download Report</h2>

        <div style={styles.dateRow}>
          <div>
            <strong>Selected Range:</strong>{" "}
            {selectedRange.startDate.toLocaleDateString("en-GB")} -{" "}
            {selectedRange.endDate.toLocaleDateString("en-GB")}
          </div>
          <div ref={pickerRef}>
            <button
              style={styles.calendarBtn}
              onClick={() => setShowPicker(!showPicker)}
            >
              <FaCalendar />
            </button>

            {showPicker && (
              <div style={styles.pickerWrapper}>
                <DateRangePicker
                  ranges={[tempRange]}
                  onChange={handleSelect}
                  maxDate={new Date()}
                  editableDateInputs
                />

                <div style={styles.pickerFooter}>
                  <button style={styles.applyBtn} onClick={handleApply}>
                    Apply
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {["super", "operation", "compliance"].includes(role) && (
          <>
            <div style={styles.section}>
              <label style={styles.label}>Organizations</label>

              <Select
                isMulti
                isLoading={loadingFilters}
                closeMenuOnSelect={false}
                options={orgOptions}
                value={selectedOrgs}
                placeholder={
                  loadingFilters
                    ? "Loading organizations..."
                    : "Select organizations"
                }
                onChange={(selected) => {
                  if (selected?.some((item) => item.value === "__all__")) {
                    setSelectedOrgs(orgOptions.slice(1));
                    return;
                  }

                  setSelectedOrgs(selected || []);
                }}
              />
            </div>

            <div style={styles.section}>
              <label style={styles.label}>Merchants</label>

              <Select
                isMulti
                isLoading={loadingFilters}
                closeMenuOnSelect={false}
                options={merchantOptions}
                value={selectedMerchants}
                placeholder={
                  loadingFilters ? "Loading merchants..." : "Select merchants"
                }
                onChange={(selected) => {
                  if (selected?.some((item) => item.value === "__all__")) {
                    setSelectedMerchants(merchantOptions.slice(1));
                    return;
                  }

                  setSelectedMerchants(selected || []);
                }}
              />
            </div>
          </>
        )}

        {/* Download button – always at the bottom, never scrolls */}
        <div style={styles.downloadWrapper}>
          <button
            style={{
              ...styles.downloadBtn,
              opacity: isDownloading ? 0.7 : 1,
              cursor: isDownloading ? "not-allowed" : "pointer",
            }}
            onClick={handleDownload}
            disabled={isDownloading}
          >
            {isDownloading ? "Generating Report..." : "Download Report"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DownloadReportModal;
